/**
 * High Grade Rarity section — digit-wheel countdown and landscape parallax.
 */

(function () {
  'use strict';

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Initialize all high-grade-rarity sections on the page.
   */
  function initSections() {
    var sections = document.querySelectorAll('.high-grade-rarity');
    sections.forEach(function (section) {
      initParallax(section);
      initOdometer(section);
    });
  }

  /**
   * Scroll parallax on landscape image only.
   * Mirrors assets/video_and_dropdown.js behaviour.
   * @param {HTMLElement} section
   */
  function initParallax(section) {
    if (REDUCED_MOTION) return;

    var parallaxContainer = section.querySelector('[data-parallax="true"]');
    if (!parallaxContainer) return;
    if (parallaxContainer.dataset.parallaxBound === 'true') return;

    var parallaxImage =
      parallaxContainer.querySelector('.high-grade-rarity__landscape-image') ||
      parallaxContainer.querySelector('img');
    if (!parallaxImage) return;

    parallaxContainer.dataset.parallaxBound = 'true';

    var speed = parseFloat(parallaxContainer.dataset.parallaxSpeed) || 8;
    var ticking = false;

    function updateParallax() {
      var rect = parallaxContainer.getBoundingClientRect();
      var windowHeight = window.innerHeight;

      if (rect.top < windowHeight && rect.bottom > 0) {
        var scrolled = (windowHeight - rect.top) / (windowHeight + rect.height);
        var translateY = (scrolled - 0.5) * speed * 45;
        parallaxImage.style.transform =
          'scale(1.15) translateY(' + translateY + 'px)';
      }
    }

    function requestParallaxUpdate() {
      if (ticking) return;
      ticking = true;

      window.requestAnimationFrame(function () {
        updateParallax();
        ticking = false;
      });
    }

    window.addEventListener('scroll', requestParallaxUpdate, { passive: true });
    window.addEventListener('resize', requestParallaxUpdate, { passive: true });
    updateParallax();
  }

  /**
   * Ease-in-out quad. Slow at the start and end, fast in the middle,
   * without the cubic lurch.
   * @param {number} t
   * @returns {number}
   */
  function easeInOutQuad(t) {
    if (t < 0.5) return 2 * t * t;
    return 1 - Math.pow(-2 * t + 2, 2) / 2;
  }

  /**
   * Tens ribbon position for a (possibly fractional) ones index.
   * Stays still unless the tens digit actually changes.
   * @param {{tensIndexByStep: number[]}} wheels
   * @param {number} onesPos
   * @param {number} totalSteps
   * @returns {number}
   */
  function tensPosition(wheels, onesPos, totalSteps) {
    var lo = Math.min(totalSteps, Math.floor(onesPos));
    var hi = Math.min(totalSteps, lo + 1);
    var frac = onesPos - lo;
    var tensLo = wheels.tensIndexByStep[lo];
    var tensHi = wheels.tensIndexByStep[hi];

    return tensLo + (tensHi - tensLo) * frac;
  }

  /**
   * 0 while tens is visible, 1 once the leading zero is fully gone.
   * @param {{tensRibbonDigits: number[]}} wheels
   * @param {number} tensPos
   * @returns {number}
   */
  function tensHideAmount(wheels, tensPos) {
    var zeroIndex = -1;
    var i;

    for (i = 0; i < wheels.tensRibbonDigits.length; i++) {
      if (wheels.tensRibbonDigits[i] === 0) {
        zeroIndex = i;
        break;
      }
    }

    if (zeroIndex <= 0) return zeroIndex === 0 ? 1 : 0;
    if (tensPos <= zeroIndex - 1) return 0;
    return Math.min(1, tensPos - (zeroIndex - 1));
  }

  /**
   * Paint ribbon positions. onesPos may be fractional for a continuous roll.
   * @param {{tens: {wheel: HTMLElement, ribbon: HTMLElement}, ones: {ribbon: HTMLElement}, tensIndexByStep: number[], tensRibbonDigits: number[]}} wheels
   * @param {number} onesPos
   * @param {number} totalSteps
   */
  function applyPosition(wheels, onesPos, totalSteps) {
    var tensPos = tensPosition(wheels, onesPos, totalSteps);
    var hide = tensHideAmount(wheels, tensPos);

    wheels.ones.ribbon.style.transform =
      'translate3d(0, -' + onesPos + 'em, 0)';
    wheels.tens.ribbon.style.transform =
      'translate3d(0, -' + tensPos + 'em, 0)';
    wheels.tens.wheel.style.maxWidth = 1 - hide + 'em';
    wheels.tens.wheel.style.opacity = String(1 - hide);
    wheels.tens.wheel.classList.toggle('is-hidden', hide >= 1);
  }

  /**
   * Build a rolling digit column.
   * @param {string} mod
   * @param {number[]} digits
   * @param {boolean} hideZero
   * @returns {{wheel: HTMLElement, ribbon: HTMLElement}}
   */
  function createWheel(mod, digits, hideZero) {
    var wheel = document.createElement('span');
    var ribbon = document.createElement('span');
    var i;
    var face;
    var digit;

    wheel.className = 'hgr-wheel hgr-wheel--' + mod;
    wheel.setAttribute('aria-hidden', 'true');
    ribbon.className = 'hgr-wheel__ribbon';

    for (i = 0; i < digits.length; i++) {
      digit = digits[i];
      face = document.createElement('span');
      face.className = 'hgr-wheel__face';
      face.textContent = hideZero && digit === 0 ? '' : String(digit);
      ribbon.appendChild(face);
    }

    wheel.appendChild(ribbon);
    return { wheel: wheel, ribbon: ribbon };
  }

  /**
   * Mount independent digit columns.
   * Ones has every tick. Tens only has faces that actually change
   * (20 → 19, then 10 → 9), so 19 → 18 leaves the tens digit still.
   * @param {HTMLElement} el
   * @param {number} start
   * @param {number} end
   * @returns {{tens: Object, ones: Object, tensIndexByStep: number[], tensRibbonDigits: number[]}}
   */
  function mountWheels(el, start, end) {
    var onesDigits = [];
    var tensRibbonDigits = [];
    var tensIndexByStep = [];
    var lastTens = null;
    var tensRibbonIndex = -1;
    var value;
    var tensDigit;
    var ones;
    var tens;

    for (value = start; value >= end; value--) {
      onesDigits.push(value % 10);
      tensDigit = Math.floor(value / 10);

      if (tensDigit !== lastTens) {
        tensRibbonDigits.push(tensDigit);
        tensRibbonIndex += 1;
        lastTens = tensDigit;
      }

      tensIndexByStep.push(tensRibbonIndex);
    }

    el.textContent = '';
    tens = createWheel('tens', tensRibbonDigits, true);
    ones = createWheel('ones', onesDigits, false);
    el.appendChild(tens.wheel);
    el.appendChild(ones.wheel);

    return {
      tens: tens,
      ones: ones,
      tensIndexByStep: tensIndexByStep,
      tensRibbonDigits: tensRibbonDigits
    };
  }

  /**
   * Continuous ease-in-out roll from start to end.
   * @param {HTMLElement} el
   * @param {Object} wheels
   * @param {number} start
   * @param {number} end
   * @param {number} duration
   */
  function runCountdown(el, wheels, start, end, duration) {
    var totalSteps = start - end;
    var startTime = null;
    var lastAnnounced = start;

    wheels.ones.wheel.classList.add('hgr-wheel--instant');
    wheels.tens.wheel.classList.add('hgr-wheel--instant');

    function frame(now) {
      var t;
      var onesPos;
      var announced;

      if (startTime === null) startTime = now;
      t = Math.min(1, (now - startTime) / duration);
      onesPos = easeInOutQuad(t) * totalSteps;
      applyPosition(wheels, onesPos, totalSteps);

      announced = Math.round(start - onesPos);
      if (announced !== lastAnnounced) {
        lastAnnounced = announced;
        el.setAttribute('aria-label', announced + ' percent');
      }

      if (t < 1) {
        window.requestAnimationFrame(frame);
        return;
      }

      applyPosition(wheels, totalSteps, totalSteps);
      wheels.ones.wheel.classList.remove('hgr-wheel--instant');
      wheels.tens.wheel.classList.remove('hgr-wheel--instant');
      el.setAttribute('aria-label', end + ' percent');
    }

    window.requestAnimationFrame(frame);
  }

  /**
   * Digit-wheel countdown from start to end when the section is in view.
   * @param {HTMLElement} section
   */
  function initOdometer(section) {
    var el = section.querySelector('.high-grade-rarity__odometer');
    if (!el) return;
    if (el.dataset.odometerBound === 'true') return;

    el.dataset.odometerBound = 'true';

    var start = parseInt(el.dataset.countdownStart, 10) || 20;
    var end = parseInt(el.dataset.countdownEnd, 10) || 1;
    var duration = parseInt(el.dataset.countdownDuration, 10) || 2800;
    duration = Math.round(duration * 2);

    el.setAttribute('aria-label', start + ' percent');

    if (REDUCED_MOTION || start <= end) {
      el.textContent = String(end);
      el.setAttribute('aria-label', end + ' percent');
      return;
    }

    var wheels = mountWheels(el, start, end);
    var totalSteps = start - end;

    wheels.ones.wheel.classList.add('hgr-wheel--instant');
    wheels.tens.wheel.classList.add('hgr-wheel--instant');
    applyPosition(wheels, 0, totalSteps);

    var hasStarted = false;
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting || hasStarted) return;
          hasStarted = true;
          observer.disconnect();
          runCountdown(el, wheels, start, end, duration);
        });
      },
      {
        threshold: 0,
        rootMargin: '0px 0px -35% 0px'
      }
    );

    observer.observe(section);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSections);
  } else {
    initSections();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.classList.contains('high-grade-rarity')
      ? event.target
      : event.target.querySelector('.high-grade-rarity');

    if (section) {
      initParallax(section);
      initOdometer(section);
    }
  });
})();
